/* =====================================================================
   Annette D. — couche de mouvement
   ---------------------------------------------------------------------
   Deux choses, reprises de la bibliothèque MotionSites et transposées
   sans framework :

   1. Les révélations au défilement : les blocs montent de quelques
      pixels en fondu quand ils entrent à l'écran, en cascade.
   2. Le carrousel de produits (section annetted-carrousel) : onglets,
      glisser à la souris, flèches et barre de progression.

   RÈGLE — rien n'est masqué par la feuille de style. Le 22 septembre,
   une règle d'apparition en CSS pur (`animation-timeline: view()`) a
   laissé huit sections de l'accueil à opacité zéro : Horizon ne fait pas
   défiler le document mais `.page-wrapper`, et la chronologie ne
   progressait jamais. Ici, c'est ce script qui pose la classe qui cache,
   au moment même où il branche l'observateur qui révèle. Sans script,
   sans IntersectionObserver, dans l'éditeur de thème ou avec « réduire
   les animations », tout reste visible.

   L'observateur n'a pas de racine explicite : il mesure par rapport à la
   fenêtre et tient compte des conteneurs qui défilent, donc de
   `.page-wrapper`.
   ===================================================================== */

(() => {
  const html = document.documentElement;
  const mouvementReduit = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editeur = html.classList.contains('shopify-design-mode');

  /* -------------------------------------------------------------------
     1. Révélations
     Chaque entrée : le groupe observé, puis les éléments qui entrent en
     cascade (sélecteur relatif au groupe ; vide = le groupe lui-même).
     Les sections maison existantes sont ciblées par leurs classes, sans
     toucher à leur Liquid.
     ------------------------------------------------------------------- */
  const GROUPES = [
    ['.annetted-entete', ''],
    ['.annetted-carrousel__entete', ''],
    ['.annetted-etapes__liste', ':scope > .annetted-etapes__item'],
    ['.annetted-categories', ':scope > li'],
    ['.annetted-produits', ':scope > .annetted-produits__item'],
    ['.annetted-bande', '.annetted-bande__visuels > *, .annetted-bande__texte'],
    ['.annetted-reassurance', ':scope > .annetted-reassurance__item'],
    ['.annetted-newsletter', ':scope > *'],
    ['.annetted-instagram__grille', ':scope > li'],
    ['.annetted-carrousel__piste', ':scope > .annetted-carrousel__item'],
    // Grilles de collection et de recherche : chaque carte est son propre
    // groupe. Le haut de la grille est souvent à l'écran au chargement ;
    // un groupe unique ne s'animerait donc jamais. Les cartes qui entrent
    // ensemble s'échelonnent par leur rang dans le lot (voir l'observateur).
    ['.product-grid-container .product-grid__item', ''],
  ];

  /* Groupes génériques, pour les sections de la refonte d'octobre : un
     conteneur `data-annetted-groupe`, et dedans les éléments qui entrent,
     marqués `data-annetted-entree` (montee, flou, ligne, filet, image —
     les primitives de annetted-mouvement.css). Un groupe sans élément
     marqué entre lui-même. Rien à ajouter ici pour une nouvelle section. */
  const GROUPE_GENERIQUE = '[data-annetted-groupe]';
  const ENTREE = '[data-annetted-entree]';

  const CACHE = 'annetted-revele';
  const VISIBLE = 'annetted-revele--visible';
  const DELAI_DEPART = 120; // ms avant le premier élément d'un groupe
  const PAS = 80; // ms entre deux éléments — la cadence du prompt « beauty-products »
  const PAS_MAX = 7; // au-delà, le reste du groupe arrive avec le 8e
  const DUREE = 900; // la plus longue transition de annetted-mouvement.css, plus une marge
  // Les primitives plus lentes : ligne masquée (1,05 s) et image (1,4 s).
  const DUREES = { ligne: 1300, image: 1600 };

  const revelationsActives = () =>
    !editeur && !mouvementReduit.matches && 'IntersectionObserver' in window;

  /** Les éléments d'un groupe, dans l'ordre du document. */
  const elementsDe = (groupe, selecteur) =>
    selecteur ? Array.from(groupe.querySelectorAll(selecteur)) : [groupe];

  /** Les éléments marqués d'un groupe générique — pas ceux d'un groupe imbriqué. */
  const entreesDe = (groupe) => {
    const entrees = Array.from(groupe.querySelectorAll(ENTREE)).filter(
      (el) => el.parentElement.closest(GROUPE_GENERIQUE) === groupe
    );
    return entrees.length ? entrees : [groupe];
  };

  /** Fait entrer une liste d'éléments en cascade, puis nettoie.
      `rang` décale le départ quand plusieurs groupes entrent ensemble. */
  const reveler = (elements, rang = 0) => {
    elements.forEach((el, i) => {
      const delai = DELAI_DEPART + Math.min(rang + i, PAS_MAX) * PAS;
      el.style.setProperty('--annetted-revele-delai', `${delai}ms`);
      el.classList.add(VISIBLE);
      // Une fois entré, l'élément redevient un élément ordinaire : plus
      // de délai de transition qui traînerait sur un survol.
      window.setTimeout(() => {
        el.classList.remove(CACHE, VISIBLE);
        el.style.removeProperty('--annetted-revele-delai');
      }, delai + (DUREES[el.dataset.annettedEntree] || DUREE));
    });
  };

  const observateur = revelationsActives()
    ? new IntersectionObserver(
        (entrees) => {
          let rang = 0;
          for (const entree of entrees) {
            if (!entree.isIntersecting) continue;
            observateur.unobserve(entree.target);
            const elements = entree.target.annettedElements || [];
            reveler(elements, rang);
            rang += elements.length;
          }
        },
        { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
      )
    : null;

  /** Prépare les groupes sous la ligne de flottaison d'une racine donnée. */
  const preparerRevelations = (racine = document) => {
    if (!observateur) return;
    const hauteur = window.innerHeight || html.clientHeight;

    const armer = (groupe, elements) => {
      if (groupe.annettedObserve) return;
      groupe.annettedObserve = true;

      // Ce qui est déjà à l'écran au chargement ne bouge pas : un
      // contenu qui disparaît puis revient sous les yeux, c'est un
      // clignotement, pas une animation.
      if (groupe.getBoundingClientRect().top < hauteur * 0.9) return;
      if (!elements.length) return;

      elements.forEach((el) => el.classList.add(CACHE));
      groupe.annettedElements = elements;
      observateur.observe(groupe);
    };

    for (const [selecteurGroupe, selecteurElements] of GROUPES) {
      racine
        .querySelectorAll(selecteurGroupe)
        .forEach((groupe) => armer(groupe, elementsDe(groupe, selecteurElements)));
    }
    racine.querySelectorAll(GROUPE_GENERIQUE).forEach((groupe) => armer(groupe, entreesDe(groupe)));
  };

  /** Rejoue l'entrée d'éléments déjà visibles (changement d'onglet). */
  const rejouer = (elements) => {
    if (!revelationsActives() || !elements.length) return;
    elements.forEach((el) => el.classList.add(CACHE));
    // Force le calcul de l'état caché avant de lancer la transition.
    void elements[0].offsetWidth;
    reveler(elements);
  };

  /* -------------------------------------------------------------------
     2. Carrousel de produits
     ------------------------------------------------------------------- */
  class AnnettedCarrousel extends HTMLElement {
    connectedCallback() {
      if (this.annettedPret) return;
      this.annettedPret = true;

      this.onglets = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panneaux = Array.from(this.querySelectorAll('.annetted-carrousel__panneau'));
      this.lien = this.querySelector('[data-annetted-lien-onglet]');
      this.pied = this.querySelector('.annetted-carrousel__pied');
      this.curseur = this.querySelector('.annetted-carrousel__curseur');
      this.precedent = this.querySelector('[data-annetted-precedent]');
      this.suivant = this.querySelector('[data-annetted-suivant]');

      this.majProgression = this.majProgression.bind(this);
      this.panneaux.forEach((panneau) => this.brancherPiste(panneau));

      this.onglets.forEach((onglet, index) => {
        onglet.addEventListener('click', () => this.activer(index, true));
        onglet.addEventListener('keydown', (event) => this.clavierOnglets(event, index));
      });

      this.precedent?.addEventListener('click', () => this.defiler(-1));
      this.suivant?.addEventListener('click', () => this.defiler(1));

      this.redimension = new ResizeObserver(() => this.majProgression());
      this.panneaux.forEach((panneau) => this.redimension.observe(panneau));

      this.majProgression();
    }

    disconnectedCallback() {
      this.redimension?.disconnect();
    }

    get piste() {
      const panneau = this.panneaux.find((p) => !p.hidden) || this.panneaux[0];
      return panneau?.querySelector('.annetted-carrousel__piste');
    }

    /* --- Onglets (motif ARIA « tabs », activation manuelle au clic,
       automatique aux flèches) --- */
    activer(index, deLUtilisateur) {
      if (!this.onglets[index]) return;

      this.onglets.forEach((onglet, i) => {
        const actif = i === index;
        onglet.setAttribute('aria-selected', String(actif));
        onglet.tabIndex = actif ? 0 : -1;
        onglet.classList.toggle('annetted-carrousel__onglet--actif', actif);
      });

      this.panneaux.forEach((panneau, i) => {
        panneau.hidden = i !== index;
      });

      const actif = this.onglets[index];
      if (this.lien && actif.dataset.lien) {
        this.lien.href = actif.dataset.lien;
        const libelle = this.lien.querySelector('[data-annetted-lien-libelle]');
        if (libelle && actif.dataset.lienTexte) libelle.textContent = actif.dataset.lienTexte;
      }

      const piste = this.piste;
      if (piste) {
        piste.scrollLeft = 0;
        if (deLUtilisateur) rejouer(Array.from(piste.querySelectorAll(':scope > .annetted-carrousel__item')));
      }
      this.majProgression();
    }

    clavierOnglets(event, index) {
      const dernier = this.onglets.length - 1;
      const cibles = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: dernier };
      if (!(event.key in cibles)) return;

      event.preventDefault();
      let cible = cibles[event.key];
      if (cible > dernier) cible = 0;
      if (cible < 0) cible = dernier;
      this.activer(cible, true);
      this.onglets[cible].focus();
    }

    /* --- Piste : glisser à la souris, progression --- */
    brancherPiste(panneau) {
      const piste = panneau.querySelector('.annetted-carrousel__piste');
      if (!piste) return;

      piste.addEventListener('scroll', this.majProgression, { passive: true });

      // Glisser à la souris seulement : au doigt et au pavé tactile, le
      // défilement natif fait déjà mieux. La molette verticale n'est PAS
      // détournée (le prompt d'origine le fait) : sur une page longue, le
      // visiteur qui descend resterait coincé dans le carrousel.
      let depart = null;
      let aGlisse = false;

      piste.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        depart = { x: event.clientX, defilement: piste.scrollLeft };
        aGlisse = false;
      });

      piste.addEventListener('pointermove', (event) => {
        if (!depart) return;
        const ecart = event.clientX - depart.x;
        if (!aGlisse && Math.abs(ecart) < 6) return;

        if (!aGlisse) {
          aGlisse = true;
          piste.setPointerCapture(event.pointerId);
          piste.classList.add('annetted-carrousel__piste--glisse');
        }
        piste.scrollLeft = depart.defilement - ecart;
      });

      const relacher = (event) => {
        if (!depart) return;
        depart = null;
        if (piste.hasPointerCapture?.(event.pointerId)) piste.releasePointerCapture(event.pointerId);
        // L'aimantation revient au relâcher : la piste se cale sur la
        // carte la plus proche.
        piste.classList.remove('annetted-carrousel__piste--glisse');
      };
      piste.addEventListener('pointerup', relacher);
      piste.addEventListener('pointercancel', relacher);

      // Un glisser ne doit pas ouvrir la fiche sur laquelle il se termine.
      piste.addEventListener(
        'click',
        (event) => {
          if (!aGlisse) return;
          event.preventDefault();
          event.stopPropagation();
          aGlisse = false;
        },
        true
      );

      // Le glisser natif des images et des liens (fantôme de l'image
      // sous le curseur) court-circuiterait le nôtre.
      piste.addEventListener('dragstart', (event) => event.preventDefault());
    }

    defiler(sens) {
      const piste = this.piste;
      if (!piste) return;
      const carte = piste.querySelector('.annetted-carrousel__item');
      const pas = carte ? carte.getBoundingClientRect().width : piste.clientWidth * 0.8;
      const cartesParPas = Math.max(1, Math.floor(piste.clientWidth / pas) - 1);
      piste.scrollBy({
        left: sens * pas * cartesParPas,
        behavior: mouvementReduit.matches ? 'auto' : 'smooth',
      });
    }

    majProgression() {
      const piste = this.piste;
      if (!piste || !this.pied) return;

      const largeurVisible = piste.clientWidth;
      const largeurTotale = piste.scrollWidth;
      const debordement = largeurTotale - largeurVisible;

      // Tout tient à l'écran : ni barre ni flèches.
      this.pied.hidden = debordement <= 2;
      if (debordement <= 2) return;

      // Le curseur a la proportion de ce qui est visible (le prompt
      // d'origine le fige à 30 %, ce qui ment sur la longueur restante).
      const part = Math.min(1, Math.max(0.12, largeurVisible / largeurTotale));
      const progression = Math.min(1, Math.max(0, piste.scrollLeft / debordement));

      if (this.curseur) {
        this.curseur.style.width = `${part * 100}%`;
        this.curseur.style.transform = `translateX(${progression * (1 / part - 1) * 100}%)`;
      }
      if (this.precedent) this.precedent.disabled = piste.scrollLeft <= 2;
      if (this.suivant) this.suivant.disabled = piste.scrollLeft >= debordement - 2;
    }
  }

  if (!customElements.get('annetted-carrousel')) {
    customElements.define('annetted-carrousel', AnnettedCarrousel);
  }

  /* -------------------------------------------------------------------
     3. Démarrage
     ------------------------------------------------------------------- */
  const demarrer = () => preparerRevelations();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer, { once: true });
  } else {
    demarrer();
  }
})();
